package com.tovtalk.presentation.viewmodel.upload

import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.repository.HashtagInfo
import com.tovtalk.domain.repository.UploadRepository
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.ViewModel
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.utils.extensions.extractHashtags

/**
 * Upload ViewModel (Reels Only Architecture)
 */
class UploadViewModel(
    private val uploadRepository: UploadRepository,
    private val postRepository: com.tovtalk.domain.repository.PostRepository,
    private val appRepository: com.tovtalk.domain.repository.AppRepository,
    private val hashtagRepository: com.tovtalk.domain.repository.HashtagRepository,
) : ViewModel() {

    private val _uploadState = MutableStateFlow<UiState<String>>(UiState.Idle)
    val uploadState: StateFlow<UiState<String>> = _uploadState.asStateFlow()
    
    private val _uploadProgress = MutableStateFlow(0f)
    val uploadProgress: StateFlow<Float> = _uploadProgress.asStateFlow()
    
    // Reel type selection
    private val _isPermanent = MutableStateFlow(true)
    val isPermanent: StateFlow<Boolean> = _isPermanent.asStateFlow()

    /** Set on successful upload; cleared on resetState. Used for "watch video" after upload. */
    private val _lastUploadedPost = MutableStateFlow<Post?>(null)
    val lastUploadedPost: StateFlow<Post?> = _lastUploadedPost.asStateFlow()

    // Hashtag support
    private val _trendingHashtags = MutableStateFlow<List<HashtagInfo>>(emptyList())
    val trendingHashtags: StateFlow<List<HashtagInfo>> = _trendingHashtags.asStateFlow()

    private val _hashtagSuggestions = MutableStateFlow<List<HashtagInfo>>(emptyList())
    val hashtagSuggestions: StateFlow<List<HashtagInfo>> = _hashtagSuggestions.asStateFlow()

    private val hashtagSearchQuery = MutableSharedFlow<String>(extraBufferCapacity = 1)

    init {
        loadTrendingHashtags()
        setupHashtagSearch()
    }

    fun setIsPermanent(value: Boolean) {
        _isPermanent.value = value
    }

    private fun loadTrendingHashtags() {
        viewModelScope.launch {
            when (val result = hashtagRepository.getTrending(limit = 8)) {
                is DomainResult.Success -> _trendingHashtags.value = result.data
                is DomainResult.Error -> { /* silent fail — trending is optional */ }
                else -> {}
            }
        }
    }

    @OptIn(FlowPreview::class, kotlinx.coroutines.ExperimentalCoroutinesApi::class)
    private fun setupHashtagSearch() {
        viewModelScope.launch {
            hashtagSearchQuery
                .debounce(300)
                .flatMapLatest { query ->
                    flow {
                        if (query.isBlank()) {
                            emit(emptyList())
                        } else {
                            when (val result = hashtagRepository.searchHashtags(query, limit = 5)) {
                                is DomainResult.Success -> emit(result.data)
                                else -> emit(emptyList())
                            }
                        }
                    }
                }
                .collect { _hashtagSuggestions.value = it }
        }
    }

    fun searchHashtags(query: String) {
        hashtagSearchQuery.tryEmit(query)
    }

    fun clearHashtagSuggestions() {
        _hashtagSuggestions.value = emptyList()
    }

    fun uploadReel(
        bytes: ByteArray,
        filename: String,
        description: String,
        isPermanent: Boolean = true,
        category: String = "posts"
    ) {
        viewModelScope.launch {
            _uploadState.value = UiState.Loading
            _uploadProgress.value = 0f
            
            // Validate it's a video file
            val isVideo = filename.lowercase().endsWith(".mp4") || 
                          filename.lowercase().endsWith(".mov") ||
                          filename.lowercase().endsWith(".avi")
            
            if (!isVideo) {
                _uploadState.value = UiState.Error("Only video files are supported. Please select a video.")
                return@launch
            }
            
            val result = uploadRepository.uploadFile(
                bytes = bytes,
                filename = filename,
                category = category,
                onProgress = { progress ->
                    _uploadProgress.value = progress
                }
            )
            
            when (result) {
                is DomainResult.Success -> {
                    _uploadProgress.value = 1f
                    
                    // Create Post
                    val createResult = postRepository.createPost(
                        caption = description,
                        videoPath = result.data.path,
                        hashtags = description.extractHashtags().map { it.lowercase() },
                        musicId = null,
                        isPermanent = isPermanent,
                        contentSource = "app"
                    )
                    
                    when (createResult) {
                        is DomainResult.Success -> {
                            val message = if (isPermanent) {
                                "Reel uploaded successfully!"
                            } else {
                                val hours = (appRepository.getAppConfig() as? DomainResult.Success)
                                    ?.data?.reelExpirationHours ?: 24
                                "Reel uploaded! It will be visible for $hours hours."
                            }
                            _lastUploadedPost.value = createResult.data
                            _uploadState.value = UiState.Success(message)
                        }
                        is DomainResult.Error -> {
                            _uploadState.value = UiState.Error("Upload successful but reel creation failed: ${createResult.message}")
                            com.tovtalk.utils.logger.Logger.e("Upload successful but reel creation failed: ${createResult.message}", "UploadViewModel")
                        }
                        else -> {
                            // Handle other states if any
                        }
                    }
                }
                is DomainResult.Error -> {
                    _uploadProgress.value = 0f
                    _uploadState.value = UiState.Error(result.message)
                }
                else -> {
                    // Loading or other states
                }
            }
        }
    }
    
    // Backward compatibility
    fun uploadMedia(
        bytes: ByteArray,
        filename: String,
        description: String,
        category: String = "posts"
    ) = uploadReel(bytes, filename, description, isPermanent = _isPermanent.value, category = category)
    
    fun resetState() {
        _uploadState.value = UiState.Idle
        _uploadProgress.value = 0f
        _lastUploadedPost.value = null
        _isPermanent.value = true
    }
}
